<template>
    <div ranking question :id="`question-${questionData.number}`" @keyup.enter="submitQuestion">
        <questionheader :questionData="questionData" />
        <draggable v-model="questionData.answers" data-question-main role="list" @end="dragged">
            <transition-group>
                <div
                    v-for="(answer, index) in questionData.answers"
                    :key="`answer-${index}`"
                    data-answer
                    role="listitem"
                    :id="`question-${questionData.number}_answer-${answer.number}`"
                    :is-checked="answer.isChecked"
                    :disabled="!get.editingAllowed(questionData) || state.global.showLoader"
                >
                    <label data-index data-answer-part>
                        <input
                            :value="answer.isChecked ? index + 1 : 0"
                            type="number"
                            @change="numberInputChanged(answer, index, $event)"
                            onfocus="this.select();"
                            @keydown="numberInputKeyDown"
                            :id="`question-${questionData.number}number-${answer.number}`"
                        />
                    </label>
                    <label data-right-label>
                        <input
                            type="checkbox"
                            data-only-screenreader
                            @change="checkboxChanged(answer)"
                            :required="answersStillRequired"
                            :id="`question-${questionData.number}_check-${answer.number}`"
                        />
                        <div data-label data-answer-part>
                            <p v-html="$sanitize(answer.label)" :id="`question-${questionData.number}_label-${answer.number}`"></p>
                        </div>
                    </label>
                </div>
            </transition-group>
        </draggable>
        <questionfooter :questionData="questionData" />
        <validationpopup :questionValidation="questionValidation" />
    </div>
</template>

<script>
import mixins from "@/global/mixins";
import draggable from "vuedraggable";

export default {
    name: `ranking-question`,
    mixins: [mixins.defaultQuestion],
    components: {
        draggable,
    },
    computed: {
        // The answers that are selected by the user
        answersGiven: function () {
            return this.questionData.answers.filter((answer) => answer.isChecked).length;
        },
        answers: function () {
            return this.questionData.answers;
        },
        // Decide whether the options have to have a required attribute added to them. In this way we can trigger
        // the browser to consider the form to be invalid as long as the right amount of options hasn't been selected
        answersStillRequired: function () {
            // Is the amount of selected within both the upper and lower limits that are allowed?
            let maxAmtValid, minAmtValid;
            let maxRestrictValue = parseInt(this.restrictions.maxValue);
            let minRestrictValue = parseInt(this.restrictions.minValue);

            if (maxRestrictValue === -1) maxAmtValid = false;
            else maxAmtValid = this.answersGiven > maxRestrictValue;

            if (minRestrictValue === -1) minAmtValid = false;
            else minAmtValid = this.answersGiven < minRestrictValue;

            return minAmtValid || maxAmtValid;
        },
    },
    methods: {
        dragged: function (e) {
            let movedItem = this.answers[e.newIndex];
            let newItemPosition = e.newDraggableIndex;

            // The dragged answer should be checked if it dragged ito a position where others are checked as well. Otherwise not.
            if (e.newDraggableIndex < e.oldDraggableIndex) {
                movedItem.isChecked = newItemPosition <= this.answersGiven;
            } else {
                movedItem.isChecked = newItemPosition < this.answersGiven;
            }

            this.updateIndexes();
        },
        // Event for when answers are clicked
        checkboxChanged: function (clickedAnswer) {
            let clickedAnswerIndex = this.answers.findIndex((answer) => answer.id === clickedAnswer.id);

            // Select or deselect the answer that is clicked and move it to the right position
            if (clickedAnswer.isChecked === false) {
                clickedAnswer.isChecked = true;

                let amtOfCheckedAnswers = this.answersGiven;
                let item = this.answers.splice(clickedAnswerIndex, 1);

                this.answers.splice(amtOfCheckedAnswers - 1, 0, item[0]);
            } else {
                let lastIndex = this.questionData.answers.map((answer) => answer.isChecked).lastIndexOf(true);
                let item = this.answers.splice(clickedAnswerIndex, 1);

                this.answers.splice(lastIndex, 0, item[0]);
                clickedAnswer.isChecked = false;
            }

            this.updateIndexes();
        },
        numberInputChanged: function (clickedAnswer, index, e) {
            const value = e.target.value;

            // We don't want empty input numbers
            if (value === `` || parseInt(value) < 1) {
                e.target.value = clickedAnswer.isChecked ? index + 1 : 0;
                return;
            }

            let clickedAnswerIndex = this.answers.findIndex((answer) => answer.id === clickedAnswer.id);
            let lastIndex = this.questionData.answers.map((answer) => answer.isChecked).lastIndexOf(true);
            let item = this.answers.splice(clickedAnswerIndex, 1);

            // If there are no answers selected yet, the input will always be at the top
            if (lastIndex === -1) lastIndex = 0;

            // Make sure we don't have gaps between the answers that are selected. Numbers that are too high, will be corrected
            let newIndex = Math.min(lastIndex, value - 1);

            clickedAnswer.isChecked = true;
            this.answers.splice(newIndex, 0, item[0]);

            this.updateIndexes();
        },
        numberInputKeyDown: function (e) {
            // We don't want the letter e in our number inputs
            if (e.code === `KeyE`) e.preventDefault();
        },
        // The backend wants to know the order of the answers by explicitly stating the index in the openField
        updateIndexes: function () {
            this.questionData.answers.forEach((answer, index) => {
                if (answer.isChecked) answer.openField =  (index + 1).toString();//The openfield is expected to be a string
                else answer.openField = ``;
            });
        },
    },
};
</script>

<style lang="scss" scoped>
[data-answer] {
    margin: 0;
    padding: 0 0 10px 0;
    align-items: stretch;

    &[disabled] {
        background: #e4e4e4;
        pointer-events: none;
        opacity: 0.5;
    }
}

[data-question-main] {
    display: flex;
    align-items: center;

    > span {
        max-width: 700px;
        width: 100%;
        flex-direction: column;
    }
}

[ranking] [data-right-label] {
    min-height: 60px;
    width: 100%;
    display: flex;
    &:last-of-type {
        margin-bottom: 0;
    }

    input[type="checkbox"] {
        width: auto;
    }

    p {
        font-size: 15px;
        color: #454848;
        margin: 0;
        font-weight: bold;
        pointer-events: none;
        -webkit-touch-callout: none;
        -webkit-user-select: none;
        -khtml-user-select: none;
        -moz-user-select: none;
        -ms-user-select: none;
        user-select: none;
    }

    [data-label] {
        width: 100%;
        padding-left: 40px;
        padding: 20px;
        margin-left: 10px;
    }

    input[type="checkbox"]:focus ~ div {
        outline: 1px dashed #333;
    }
}

[data-answer-part] {
    background: #fff;
    color: #fff;
    display: flex;
    align-items: center;
    border-radius: 5px;
    border: 2px solid #454848;
    cursor: pointer;

    &:hover {
        transition: 0.4s;
        background: #ddd;
    }
}

[data-index] {
    justify-content: center;
    min-width: 60px;
    padding: 12px 20px;

    input[type="number"] {
        color: #333;
        text-align: center;
        background: transparent;
        width: 40px;
        padding: 8px 10px;
        border: 0;
        border-bottom: 1px dashed;
    }
}

[is-checked] {
    [data-answer-part] {
        background: var(--primary-color);
        color: #fff;
        border: 2px solid var(--primary-color);
        transition: 0.2s;

        p {
            color: #fff;
        }

        input[type="number"] {
            color: #fff;
        }
    }

    [data-answer-part] {
        &:hover {
            background: var(--primary-color);
            filter: brightness(115%);
        }
    }
}

input::-webkit-outer-spin-button,
input::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
}

input[type="number"] {
    -moz-appearance: textfield;
}
</style>